import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useContext,
  useEffect,
  useState
} from 'react'
import {socket} from "../index"
import { useTheme } from './theme'
import { useAuth } from './auth'
import makeToast from '../components/toast'

interface UsersContext {
  allFilteredUsers: User[],
  users: User[],
  usersCount: number,
  unassignedUsersCount: number,
  error: string,
  search: string,
  sortByName: "default" | "ascending" | "descending",
  sortByEmail: "default" | "ascending" | "descending",
  filterByStatus: string,
  filterByRole: string,
  displayMode: string,
  pagesCount: number,
  currentPage: number,
  resultsListLength: number,
  itemsOnPage: number,
  selectedUser: User | null,
  unassigned: boolean,
  setSearch: Dispatch<SetStateAction<string>>,
  setSortByName: Dispatch<SetStateAction<"default" | "ascending" | "descending">>,
  setSortByEmail: Dispatch<SetStateAction<"default" | "ascending" | "descending">>,
  setFilterByStatus: Dispatch<SetStateAction<string>>,
  setFilterByRole: Dispatch<SetStateAction<string>>,
  setDisplayMode: Dispatch<SetStateAction<string>>,
  setCurrentPage: Dispatch<SetStateAction<number>>,
  setItemsOnPage: Dispatch<SetStateAction<number>>,
  setSelectedUser: Dispatch<SetStateAction<User | null>>
  setUnassigned: Dispatch<SetStateAction<boolean>>
}

export interface User {
  id: string
  availableSpeechTranslationFiles: string
  availableWebInterfaceTranslationFiles: string
  availableIBTGTranslationFiles: string
  companies: string
  company: string
  displayName: string
  email: string
  phoneNumber: string
  photoURL: string
  serialNumbers: string
  manufacturer: string
  role: string
  signedIn: boolean
  removed: 'false' | 'true'
}

const Users = createContext({} as UsersContext)

export default function UsersProvider(
  { children }: { children: ReactNode }
) {
  const [allFilteredUsers, setAllFilteredUsers] = useState<User[]>([])
  const [users, setUsers] = useState<User[]>([])
  const [usersCount, setUsersCount] = useState<number>(0)
  const [unassignedUsersCount, setUnassignedUsersCount] = useState<number>(0)
  const [error, setError] = useState<string>('')
  const [search, setSearch] = useState<string>('')
  const [sortByName, setSortByName] = useState<"default" | "ascending" | "descending">("default")
  const [sortByEmail, setSortByEmail] = useState<"default" | "ascending" | "descending">("default")
  const [filterByStatus, setFilterByStatus] = useState<string>("")
  const [filterByRole, setFilterByRole] = useState<string>("")
  const [displayMode, setDisplayMode] = useState<string>(() => {
    const savedDisplayMode = localStorage.getItem("usersDisplayMode");
    return JSON.parse(savedDisplayMode!) || "list";
  })
  const [pagesCount, setPagesCount] = useState<number>(0)
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [resultsListLength, setResultsListLength] = useState<number>(0)
  const [itemsOnPage, setItemsOnPage] = useState<number>(15)
  const {user, usersNotifications, currentRole} = useAuth()
  const {mode} = useTheme()
  const [selectedUser, setSelectedUser] = useState<User | null>(null)
  const [unassigned, setUnassigned] = useState<boolean>(true)

  const updateUsers = (
    search: string,
    currentPage: number,
    itemsOnPage: number,
    sortByName: string,
    sortByEmail: string,
    filterByStatus: string,
    filterByRole: string
  ) => {
    socket.emit('get_users', search, currentPage, itemsOnPage, sortByName.toLowerCase(), sortByEmail.toLowerCase(), filterByStatus.toLowerCase(), filterByRole.toLowerCase(), unassigned)
  }

  useEffect(() => {
    const usersReceived = function (data: any) {
      if (data.error) {
        if (currentRole !== undefined && currentRole === "Global Administrator" && usersNotifications === "on") {
          makeToast('error', 'An error has occured.', mode)
        }
        setError(data.error)
      } else {
        if (selectedUser) {
          let updatedSelectedUser = data.allUsers.find((usersIterator: User) => usersIterator.id === selectedUser?.id)
          setSelectedUser(updatedSelectedUser)
        }

        if (data.usersPagesCount < currentPage) {
          setCurrentPage(1)
        }
        setAllFilteredUsers(data.allFilteredUsers || [])
        setUsers(data.users || [])
        setUsersCount(data.usersCount)
        setUnassignedUsersCount(data.unassignedUsersCount)
        setPagesCount(data.usersPagesCount)
        setResultsListLength(data.searchResults)
      }
    }
    const userModified = function (data: any) {
      if (!data.error) {
        if (currentRole !== undefined && currentRole === "Global Administrator" && usersNotifications === "on" && data.userId !== user!.uid) {
          if (data.signedIn !== undefined) {
            makeToast('success', `User ${data.signedIn ? 'signed in' : 'signed out'}\n${data.displayName.includes('@') ? 'Email' : 'Name'}: ${data.displayName}`, mode)
          } else {
            makeToast('success', `User modified\n${data.displayName.includes('@') ? 'Email' : 'Name'}: ${data.displayName}`, mode)
          }
        }
      }
    }
    const userRemoved = function (data: any) {
      if (!data.error) {
        if (currentRole !== undefined && currentRole === "Global Administrator" && usersNotifications === "on") {
          makeToast('success', `User's account removed\n${data.displayName.includes('@') ? 'Email' : 'Name'}: ${data.displayName}`, mode)
        }
      }
    }
    socket.on('users_received', usersReceived)
    socket.on('user_modified', userModified)
    socket.on('user_removed', userRemoved)
    return () => {
      socket.off('users_received', usersReceived)
      socket.off('user_modified', userModified)
      socket.off('user_removed', userRemoved)
    }
    // eslint-disable-next-line
  }, [mode, currentRole, usersNotifications, currentPage, itemsOnPage, user, selectedUser, unassigned])

  useEffect(() => {
    updateUsers(search, currentPage, itemsOnPage, sortByName, sortByEmail, filterByStatus, filterByRole)
    return () => {
      socket.off('users_list_modified')
    }
    // eslint-disable-next-line
  }, [search, currentPage, itemsOnPage, sortByName, sortByEmail, filterByStatus, filterByRole, unassigned])

  useEffect(() => {
    socket.on('users_list_modified', () => {
      updateUsers(search, currentPage, itemsOnPage, sortByName, sortByEmail, filterByStatus, filterByRole)
    })
    return () => {
      socket.off('users_list_modified')
    }
    // eslint-disable-next-line
  }, [search, currentPage, itemsOnPage, sortByName, sortByEmail, filterByStatus, filterByRole, currentRole, usersNotifications, mode, unassigned])

  useEffect(() => {
    localStorage.setItem("usersDisplayMode", JSON.stringify(displayMode))
  }, [displayMode])

  return (
    <Users.Provider
      value={{
        allFilteredUsers,
        users,
        usersCount,
        unassignedUsersCount,
        error,
        search,
        sortByName,
        sortByEmail,
        filterByStatus,
        filterByRole,
        displayMode,
        pagesCount,
        currentPage,
        resultsListLength,
        itemsOnPage,
        selectedUser,
        unassigned,
        setSearch,
        setSortByName,
        setSortByEmail,
        setFilterByStatus,
        setFilterByRole,
        setDisplayMode,
        setCurrentPage,
        setItemsOnPage,
        setSelectedUser,
        setUnassigned
      }}
    >
      {children}
    </Users.Provider>
  )
}

export const useUsers = () => useContext(Users)